"use client"

import { useState, useRef, useEffect } from "react"
import { X, Mic, MicOff, Phone, PhoneOff, Volume2, VolumeX, Loader2, CheckCircle } from "lucide-react"
import { uploadRecording } from "@/lib/upload"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"

interface PreviewModalProps {
  isOpen: boolean
  onClose: () => void
  agentName: string
  onRecordingReady?: (blob: Blob) => void
}

export function PreviewModal({ isOpen, onClose, agentName, onRecordingReady }: PreviewModalProps) {
  const [isStarted, setIsStarted] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  const [callDuration, setCallDuration] = useState(0)
  const [recordingReady, setRecordingReady] = useState<Blob | null>(null)
  const [evaluating, setEvaluating] = useState(false)
  const [previousDuration, setPreviousDuration] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])

  // Timer
 useEffect(() => {
    let interval: any;
    if (isStarted && !recordingReady) {
        interval = setInterval(() => {
          setCallDuration((prev) => prev + 1)
        }, 1000)
      }
    return () => clearInterval(interval)
  }, [isStarted, recordingReady])

  // Mic visualization
  useEffect(() => {
    if (isRecording && !isMuted) {
      const interval = setInterval(() => {
        if (analyserRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
          analyserRef.current.getByteFrequencyData(dataArray)
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length
          setAudioLevel(average / 255)
        }
      }, 100)
      return () => clearInterval(interval)
    } else {
      setAudioLevel(0)
    }
  }, [isRecording, isMuted])

  const startPreview = async () => {
    try {
      const savedConfig = (() => {
        try {
          const s = localStorage.getItem("voiceAgent.config")
          return s ? JSON.parse(s) : null
        } catch (e) {
          return null
        }
      })()
      const savedSystem = (() => {
        try {
          return localStorage.getItem("voiceAgent.systemPrompt") || null
        } catch (e) {
          return null
        }
      })()

      const config = savedConfig || { host: "api.voice-agent.ai", sampleRate: "16000", agentName }
      const systemPrompt = savedSystem || ""

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream

      const sampleRate = Number(config.sampleRate || 16000)
      const audioContext = new AudioContext({ sampleRate })
      audioContextRef.current = audioContext

      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyserRef.current = analyser

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      // 🔹 Mix mic + agent audio for recording (moved earlier so playNext can connect to agentGain)
      const mixedDest = audioContext.createMediaStreamDestination()
      source.connect(mixedDest) // mic routed into the mixed output
      const agentGain = audioContext.createGain()
      agentGain.connect(mixedDest) // agent audio will be routed into the mixed output

      // Audio queue for agent playback
      const audioQueue: AudioBuffer[] = []
      let isPlaying = false

      const playNext = async () => {
        if (isPlaying) return
        const buffer = audioQueue.shift()
        if (!buffer) return
        isPlaying = true
        const node = audioContext.createBufferSource()
        node.buffer = buffer
        // route agent audio both to the user's speakers and into the recorder's mixed stream
        node.connect(audioContext.destination)
        node.connect(agentGain)
        node.onended = () => {
          isPlaying = false
          playNext()
        }
        node.start()
      }

      const base64ToAudioBuffer = async (base64: string, sr = sampleRate) => {
        const binary = atob(base64)
        const len = binary.length / 2
        const float32 = new Float32Array(len)
        for (let i = 0; i < len; i++) {
          const lo = binary.charCodeAt(i * 2)
          const hi = binary.charCodeAt(i * 2 + 1)
          const val = (hi << 8) | lo
          const int16 = val >= 0x8000 ? val - 0x10000 : val
          float32[i] = int16 / 32768
        }
        const buffer = audioContext.createBuffer(1, float32.length, sr)
        buffer.getChannelData(0).set(float32)
        return buffer
      }

      const ws = new WebSocket(`wss://${config.host}/agents/${config.agentName}?sample-rate=${sampleRate}`)
      let sequenceNumber = 1
      let wsReady = false
      console.log("WebSocket connecting...", systemPrompt)
      // return;
      ws.onopen = () => {
        wsReady = true
        ws.send(JSON.stringify({ event: "connected" }))
        ws.send(JSON.stringify({
          event: "start",
          start: {
            from: config.phoneNumber,
            streamSid: `stream_${Date.now()}`,
            mediaFormat: {
              encoding: "audio/x-l16",
              sampleRate: sampleRate,
              channels: 1,
            },
            custom_parameters: {
              org_name: config.organizationName,
              system_prompt: systemPrompt,
              initial_prompt: config.initialPrompt,
            },
          },
        }))
      }

      ws.onmessage = async (ev) => {
        if (typeof ev.data === "string") {
          const parsed = JSON.parse(ev.data)
          if (parsed.event === "media" && parsed.media?.payload) {
            const buf = await base64ToAudioBuffer(parsed.media.payload)
            audioQueue.push(buf)
            playNext()
          }
        }
      }

      ws.onerror = console.error
      ws.onclose = () => {
        wsReady = false
        console.log("WebSocket closed")
      }

      // Audio processing
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      source.connect(processor)
      processor.connect(audioContext.destination)

      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer.getChannelData(0)
        const buffer = new ArrayBuffer(inputBuffer.length * 2)
        const view = new DataView(buffer)
        let offset = 0
        for (let i = 0; i < inputBuffer.length; i++) {
          let s = Math.max(-1, Math.min(1, inputBuffer[i]))
          s = s < 0 ? s * 0x8000 : s * 0x7fff
          view.setInt16(offset, s, true)
          offset += 2
        }
        let binary = ''
        const dv = new Uint8Array(buffer)
        for (let i = 0; i < dv.length; i++) binary += String.fromCharCode(dv[i])
        const base64 = btoa(binary)
        ws.send(JSON.stringify({ event: "media", sequenceNumber: sequenceNumber++, media: { payload: base64 } }))
      }

      // Recorder: use the mixedDest created earlier (removed duplicate creation here)
      const recorder = new MediaRecorder(mixedDest.stream)
      recorderRef.current = recorder
      recordedChunksRef.current = []

      recorder.ondataavailable = (e) => {
        console.log("Recording chunk:", e.data)
        recordedChunksRef.current.push(e.data)
      }
      recorder.start()
      console.log("Recording started")

      setIsStarted(true)
      setIsRecording(true)
      setRecordingReady(null)

      ;(window as any)._voiceAgentPreview = { ws, processor, audioContext, mediaStream: stream }

    } catch (error) {
      console.error("Preview init error:", error)
      alert("Mic permission or config issue. Please retry.")
    }
  }

  const endCall = () => {
    setPreviousDuration(callDuration);
    try {
      const recorder = recorderRef.current
      if (recorder && recorder.state !== "inactive") {
        recorder.stop()
        recorder.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" })
          console.log("Recording ready:", blob)
          setRecordingReady(blob)
          onRecordingReady?.(blob)
        }
      }
    } catch (e) { console.error(e) }

    try {
      const p = (window as any)._voiceAgentPreview
      if (p?.ws?.readyState === WebSocket.OPEN) {
        p.ws.send(JSON.stringify({ event: "stop" }))
        p.ws.close()
      }
    } catch {}

    if (mediaStreamRef.current)
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())

    if (audioContextRef.current)
      audioContextRef.current.close()

    setIsRecording(false)
    setCallDuration(0)
    setAudioLevel(0)
  }

  const downloadRecording = () => {
    if (!recordingReady) return
    // Show inline Alert and close dialog immediately
    setEvaluating(true)
    

    // Start upload (don't block UI long — we await so we can log any errors)
    ;(async () => {
      try {
        console.log("Uploading preview recording...")
        await uploadRecording(recordingReady, { evalSuiteId: 'Web Agent', agentName })
        console.log("Upload complete")
      } catch (err) {
        console.error("Upload failed:", err)
      } finally {
        // Trigger browser download regardless of upload result
        // const url = URL.createObjectURL(recordingReady)
        // const a = document.createElement("a")
        // a.href = url
        // a.download = `${agentName}-preview.webm`
        // a.click()
        // URL.revokeObjectURL(url)
      }
    })()
    // Close the dialog but keep the alert visible briefly in-case the parent UI needs to show progress
    onClose()
    // Hide the evaluating alert after a short delay (optional)
    setTimeout(() => setEvaluating(false), 3000)
  }

  const toggleMute = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = isMuted
      })
    }
    setIsMuted(!isMuted)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  useEffect(() => {
    if (!isOpen) {
      setIsStarted(false)
      setIsRecording(false)
      setCallDuration(0)
      setIsMuted(false)
      setAudioLevel(0)
      setRecordingReady(null)
    }
  }, [isOpen])

  // ensure we clean up audio/ws when the dialog is closed via any UI action
  const handleClose = () => {
    try {
      // endCall is safe to call even if not started
      endCall()
    } catch (e) {
      console.error("Error during cleanup on close:", e)
    }
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="sm:max-w-lg" showCloseButton={false}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Preview Agent</DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {!isStarted ? (
          <div className="flex flex-col items-center justify-center py-8 gap-6">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
              <Phone className="h-12 w-12 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">{agentName}</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Test your AI voice agent in real-time.
              </p>
            </div>
            <Button onClick={startPreview} size="lg" className="gap-2">
              <Phone className="h-4 w-4" />
              Start Preview
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center py-8 gap-6">
            <div className="relative">
              <div
                className="w-32 h-32 rounded-full bg-primary/20 flex items-center justify-center transition-all duration-300"
                style={{ transform: `scale(${1 + audioLevel * 0.3})` }}
              >
                <div className="w-24 h-24 rounded-full bg-primary/40 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                    {isRecording && !isMuted ? (
                      <Mic className="h-8 w-8 text-primary-foreground" />
                    ) : (
                      <MicOff className="h-8 w-8 text-primary-foreground" />
                    )}
                  </div>
                </div>
              </div>

              {isRecording && (
                <span className="absolute -bottom-2 bg-red-600 text-white text-[10px] px-2 py-[2px] rounded-full animate-pulse">
                  Recording
                </span>
              )}

              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-background border rounded-full px-3 py-1">
               { !recordingReady ?  <span className="text-xs">{formatDuration(callDuration)}</span> :  <span className="text-xs">{formatDuration(previousDuration)}</span> }
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold">Connected to {agentName}</h3>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" className="h-12 w-12 rounded-full" onClick={toggleMute}>
                {isMuted ? <VolumeX /> : <Volume2 />}
              </Button>

              {!recordingReady ? (
                <Button variant="destructive" size="icon" className="h-16 w-16 rounded-full" onClick={endCall}>
                  <PhoneOff />
                </Button>
              ) : (
                <Button size="icon" className="h-16 w-16" onClick={downloadRecording}>
                  Evaluate
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
